import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useReadyForBillingWorkOrderCount } from "features/WorkOrders/hooks/useReadyForBillingWorkOrderCount";
import { setReadyForBillingWorkOrderCountRefetch } from "features/WorkOrders/workOrderReadyForBillingCountInvalidate";

export type ReadyForBillingWorkOrderCountContextValue = {
  count: number;
  isLoading: boolean;
  refetch: () => void;
};

const ReadyForBillingWorkOrderCountContext =
  createContext<ReadyForBillingWorkOrderCountContextValue | null>(null);

const fallback: ReadyForBillingWorkOrderCountContextValue = {
  count: 0,
  isLoading: false,
  refetch: () => {},
};

export function ReadyForBillingWorkOrderCountProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { can } = useAccess();
  const enabled = can(Permissions.WORK_ORDER.SUBMIT_BILLING);
  const { count, isLoading, refetch } = useReadyForBillingWorkOrderCount(enabled);

  useEffect(() => {
    if (!enabled) {
      setReadyForBillingWorkOrderCountRefetch(null);
      return;
    }
    setReadyForBillingWorkOrderCountRefetch(refetch);
    return () => {
      setReadyForBillingWorkOrderCountRefetch(null);
    };
  }, [enabled, refetch]);

  const value = useMemo(
    () => ({ count, isLoading, refetch }),
    [count, isLoading, refetch],
  );

  return (
    <ReadyForBillingWorkOrderCountContext.Provider value={value}>
      {children}
    </ReadyForBillingWorkOrderCountContext.Provider>
  );
}

export function useReadyForBillingWorkOrderCountOptional(): ReadyForBillingWorkOrderCountContextValue {
  const ctx = useContext(ReadyForBillingWorkOrderCountContext);
  return ctx ?? fallback;
}
